import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import {
  ArrowLeft, User, Buildings, ChatText, Sparkle, PresentationChart, FileText,
  ClipboardText, Note, Warning, ChartBar, Question, FolderOpen, DownloadSimple,
  CheckCircle, ArrowRight, Package,
} from "@phosphor-icons/react";
import InvestmentProfileTab from "./tabs/InvestmentProfileTab";
import AIAnalysisTab from "./tabs/AIAnalysisTab";
import DocViewTab from "./tabs/DocViewTab";
import PitchDeckTab from "./tabs/PitchDeckTab";
import FinancialHighlightsTab from "./tabs/FinancialHighlightsTab";
import InvestorQATab from "./tabs/InvestorQATab";
import DataRoomTab from "./tabs/DataRoomTab";
import ExportCenterTab from "./tabs/ExportCenterTab";

const TABS = [
  { key: "profile",           label: "Investment Profile",   icon: User },
  { key: "analysis",          label: "AI Analysis",          icon: Sparkle },
  { key: "pitch_deck",        label: "Pitch Deck",           icon: PresentationChart },
  { key: "exec_summary",      label: "Executive Summary",    icon: FileText },
  { key: "one_pager",         label: "One Pager",            icon: Note },
  { key: "memo",              label: "Investment Memorandum",icon: ClipboardText },
  { key: "due_diligence",     label: "Due Diligence Report", icon: Warning },
  { key: "financial",         label: "Financial Highlights", icon: ChartBar },
  { key: "qa",                label: "Investor Q&A",         icon: Question },
  { key: "data_room",         label: "Data Room",            icon: FolderOpen },
  { key: "export",            label: "Export Center",        icon: DownloadSimple },
];

export default function InvestmentDocDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [config, setConfig] = useState(null);
  const [tab, setTab] = useState("profile");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [d, c] = await Promise.all([
        api.get(`/investment-docs/${id}`),
        config ? Promise.resolve({ data: config }) : api.get("/investment-docs/config"),
      ]);
      setDoc(d.data);
      if (!config) setConfig(c.data);
    } catch (err) {
      toast.error("Investment doc not found");
      navigate("/investment-docs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const saveProfile = async (patch) => {
    try {
      const { data } = await api.patch(`/investment-docs/${id}`, { profile: patch });
      setDoc(data);
      toast.success("Profile saved");
    } catch (err) { toast.error("Save failed"); }
  };

  const analyse = async () => {
    if (busy) return;
    setBusy(true);
    const t = toast.loading("Generating institutional-grade analysis (30-60s)…");
    try {
      await api.post(`/investment-docs/${id}/analyse`);
      // Poll for completion
      let attempts = 0;
      while (attempts < 40) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data } = await api.get(`/investment-docs/${id}`);
        if (data.status === "analysed") {
          setDoc(data);
          toast.success("AI analysis complete", { id: t });
          setTab("analysis");
          return;
        }
        if (data.status === "failed") {
          toast.error("Analysis failed — please retry", { id: t });
          setDoc(data);
          return;
        }
        attempts++;
      }
      toast.error("Analysis is taking longer than expected — please refresh in a minute", { id: t });
    } catch (err) {
      toast.error(extractApiError(err, "Analysis failed"), { id: t });
    } finally {
      setBusy(false);
    }
  };

  const generateQA = async () => {
    if (busy) return;
    setBusy(true);
    const t = toast.loading("Generating 100 investor Q&A (60-90s)…");
    try {
      await api.post(`/investment-docs/${id}/generate-qa`);
      let attempts = 0;
      while (attempts < 40) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data } = await api.get(`/investment-docs/${id}`);
        if (data.qa_status === "ready" || (data.qa || []).length > 0) {
          setDoc(data);
          toast.success(`${(data.qa || []).length} answers generated`, { id: t });
          return;
        }
        if (data.qa_status === "failed") {
          toast.error("Q&A generation failed — please retry", { id: t });
          return;
        }
        attempts++;
      }
      toast.error("Q&A is taking longer than expected", { id: t });
    } catch (err) {
      toast.error(extractApiError(err, "Q&A generation failed"), { id: t });
    } finally {
      setBusy(false);
    }
  };

  const generatePackage = async () => {
    if (busy) return;
    setBusy(true);
    const t = toast.loading("Building your investor package (ZIP)…");
    try {
      const resp = await api.post(`/investment-docs/${id}/generate-package`, {}, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(doc?.company_name || "orixa-invest").replace(/\s+/g, "-").toLowerCase()}-investor-package.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Investor package ready", { id: t });
    } catch (err) {
      toast.error(extractApiError(err, "Package build failed"), { id: t });
    } finally {
      setBusy(false);
    }
  };

  if (loading || !doc) return <div className="max-w-6xl mx-auto p-8 text-sm text-muted-foreground">Loading…</div>;

  const analysed = !!doc.pack;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10" data-testid="idoc-detail-page">
      <Link to="/investment-docs" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 mb-6">
        <ArrowLeft size={14} /> Back to investment docs
      </Link>

      <PageHeader
        eyebrow={config?.templates?.find((t) => t.key === doc.template)?.label || "Institutional"}
        title={doc.company_name}
        subtitle={analysed ? `Analysed ${new Date(doc.last_analysed_at).toLocaleString()}` : "Complete the Investment Profile then run AI Analysis to unlock all documents."}
        action={
          <div className="flex flex-wrap gap-2">
            <button
              onClick={analyse}
              disabled={busy}
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed card-elev transition-colors"
              data-testid="idoc-analyse-btn"
            >
              <Sparkle size={14} weight="bold" /> {analysed ? "Re-run analysis" : "Run AI analysis"}
            </button>
            {analysed && (
              <button
                onClick={generatePackage}
                disabled={busy}
                className="inline-flex items-center gap-2 bg-foreground text-background rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-foreground/90 disabled:opacity-60 card-elev transition-colors"
                data-testid="idoc-package-btn"
              >
                <Package size={14} weight="bold" /> Investor package (ZIP)
              </button>
            )}
          </div>
        }
      />

      {/* Tabs */}
      <div className="mt-8 border-b border-border overflow-x-auto no-scrollbar">
        <div className="flex gap-1 min-w-max">
          {TABS.map((t) => {
            const disabled = t.key !== "profile" && t.key !== "analysis" && !analysed;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => !disabled && setTab(t.key)}
                disabled={disabled}
                className={`px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors inline-flex items-center gap-2 ${
                  active ? "border-primary text-primary" : disabled ? "border-transparent text-muted-foreground/50 cursor-not-allowed" : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                }`}
                data-testid={`idoc-tab-${t.key}`}
              >
                <t.icon size={14} weight={active ? "fill" : "regular"} />
                {t.label}
                {disabled && <span className="text-[10px] font-normal opacity-60">locked</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab bodies */}
      <div className="mt-8">
        {tab === "profile" && (
          <InvestmentProfileTab profile={doc.profile || {}} onSave={saveProfile} onAnalyse={analyse} busy={busy} />
        )}
        {tab === "analysis" && (
          <AIAnalysisTab doc={doc} config={config} onAnalyse={analyse} busy={busy} />
        )}
        {tab === "pitch_deck" && (
          <PitchDeckTab doc={doc} />
        )}
        {tab === "exec_summary" && (
          <DocViewTab
            kind="exec-summary"
            doc={doc}
            title="Executive Summary"
            renderBody={(pack) => (
              <div className="space-y-4">
                <div className="text-thesis italic text-lg font-display text-primary">{pack.executive_summary?.headline}</div>
                {(pack.executive_summary?.paragraphs || []).map((p, i) => (
                  <p key={i} className="text-sm leading-relaxed text-foreground">{p}</p>
                ))}
              </div>
            )}
            exports={[{ fmt: "pdf" }, { fmt: "docx" }]}
          />
        )}
        {tab === "one_pager" && (
          <DocViewTab
            kind="one-pager"
            doc={doc}
            title="Investor One-Pager"
            renderBody={(pack) => {
              const op = pack.one_pager || {};
              return (
                <div className="space-y-6">
                  {op.tagline && <div className="text-lg font-display italic text-primary">{op.tagline}</div>}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <OPBlock title="Company snapshot">{op.company_snapshot}</OPBlock>
                    <OPBlock title="The ask">{op.ask}</OPBlock>
                    <OPBlock title="Problem">{op.problem}</OPBlock>
                    <OPBlock title="Solution">{op.solution}</OPBlock>
                    <OPBlock title="Market">{op.market}</OPBlock>
                    <OPBlock title="Traction">
                      <ul className="list-disc list-inside space-y-1">{(op.traction_highlights || []).map((b, i) => <li key={i}>{b}</li>)}</ul>
                    </OPBlock>
                    <OPBlock title="Financials" wide>
                      <ul className="list-disc list-inside space-y-1">{(op.financial_snapshot || []).map((b, i) => <li key={i}>{b}</li>)}</ul>
                    </OPBlock>
                  </div>
                </div>
              );
            }}
            exports={[{ fmt: "pdf" }]}
          />
        )}
        {tab === "memo" && (
          <DocViewTab
            kind="memo"
            doc={doc}
            title="Investment Memorandum"
            renderBody={(pack) => (
              <div className="space-y-6">
                {pack.investment_thesis_headline && (
                  <div className="text-lg font-display italic text-primary border-l-4 border-primary pl-4">
                    {pack.investment_thesis_headline}
                  </div>
                )}
                {(pack.investment_memorandum?.sections || []).map((s, i) => (
                  <div key={i}>
                    <div className="text-overline text-primary mb-2">Section {String(i + 1).padStart(2, "0")}</div>
                    <div className="font-display text-xl tracking-tight mb-3">{s.title}</div>
                    <div className="text-sm leading-relaxed prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: s.content_html || "" }} />
                  </div>
                ))}
              </div>
            )}
            exports={[{ fmt: "pdf" }, { fmt: "docx" }]}
          />
        )}
        {tab === "due_diligence" && (
          <DocViewTab
            kind="due-diligence"
            doc={doc}
            title="Due Diligence Report"
            renderBody={(pack) => {
              const dd = pack.due_diligence || {};
              const groups = [
                ["Strengths", "strengths", "teal"],
                ["Weaknesses", "weaknesses", "amber"],
                ["Opportunities", "opportunities", "sky"],
                ["Threats", "threats", "rose"],
                ["Legal Risks", "legal_risks", "slate"],
                ["Operational Risks", "operational_risks", "slate"],
                ["Commercial Risks", "commercial_risks", "slate"],
                ["Technology Risks", "technology_risks", "slate"],
                ["Financial Risks", "financial_risks", "slate"],
                ["Governance Risks", "governance_risks", "slate"],
                ["Recommendations", "recommendations", "teal"],
              ];
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {groups.map(([label, key, tone]) => {
                    const items = dd[key] || [];
                    if (items.length === 0) return null;
                    return (
                      <div key={key} className="bg-card border border-border rounded-xl p-5" data-testid={`dd-${key}`}>
                        <div className={`text-overline mb-3 ${tone === "teal" ? "text-teal" : tone === "amber" ? "text-amber-600" : tone === "sky" ? "text-sky-600" : tone === "rose" ? "text-rose-600" : "text-muted-foreground"}`}>{label}</div>
                        <ul className="space-y-2 text-sm text-foreground">
                          {items.map((it, i) => <li key={i} className="flex gap-2"><span className="text-primary flex-shrink-0">•</span><span>{it}</span></li>)}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              );
            }}
            exports={[{ fmt: "pdf" }, { fmt: "docx" }]}
          />
        )}
        {tab === "financial" && <FinancialHighlightsTab doc={doc} />}
        {tab === "qa" && <InvestorQATab doc={doc} onGenerate={generateQA} busy={busy} />}
        {tab === "data_room" && <DataRoomTab doc={doc} />}
        {tab === "export" && <ExportCenterTab doc={doc} onPackage={generatePackage} busy={busy} />}
      </div>
    </div>
  );
}

function OPBlock({ title, children, wide }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <div className="text-overline text-muted-foreground mb-2">{title}</div>
      <div className="text-sm text-foreground leading-relaxed">{children || "—"}</div>
    </div>
  );
}
